import { supabase } from '@/lib/supabase';
import type { TrainingVideo, VideoAnnotation } from '@/types/videoAnnotations';
import type { RollReview } from '../hooks/useRollReviewPayment';

const INSTRUCTOR_REVIEW_SELECT = `
  *,
  uploader:profiles!uploader_id(full_name, username, profile_picture_url, belt_rank),
  subject:profiles!subject_id(full_name, username, profile_picture_url, belt_rank),
  reviewer:profiles!reviewer_id(full_name, username, profile_picture_url, belt_rank)
`;

export const rollReviewRepository = {
  async fetchInstructorReviewVideos(instructorId: string): Promise<TrainingVideo[]> {
    const { data, error } = await supabase
      .from('training_videos')
      .select(INSTRUCTOR_REVIEW_SELECT)
      .eq('reviewer_id', instructorId)
      .neq('status', 'archived')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as TrainingVideo[];
  },

  async linkUploadedVideo(rollReviewId: string, videoId: string): Promise<void> {
    const { error } = await supabase
      .from('roll_reviews')
      .update({ training_video_id: videoId })
      .eq('id', rollReviewId)
      .eq('status', 'paid');

    if (error) throw error;
  },

  async fetchRollReviewById(rollReviewId: string): Promise<RollReview | null> {
    const { data, error } = await supabase
      .from('roll_reviews')
      .select('*')
      .eq('id', rollReviewId)
      .single();

    if (error) throw error;
    return data;
  },

  async fetchVideoAnnotations(videoId: string): Promise<VideoAnnotation[]> {
    const { data, error } = await supabase
      .from('video_annotations')
      .select(`
        *,
        author:profiles!author_id(full_name, username, profile_picture_url)
      `)
      .eq('video_id', videoId)
      .order('timestamp_seconds', { ascending: true });

    if (error) throw error;
    return (data ?? []) as VideoAnnotation[];
  },

  /**
   * Best-effort cleanup of an uploaded-but-not-yet-paid review: removes the
   * storage object + the training_videos row. Both calls swallow errors and
   * report them in the return value so the caller can navigate forward even
   * if cleanup partially fails (orphans are acceptable — a separate cleanup
   * routine handles them — see docs/video-upload-recovery.md gap G2).
   */
  async discardPendingVideo(
    videoId: string,
    storagePath: string,
  ): Promise<{ ok: boolean; storageError?: string; rowError?: string }> {
    let storageError: string | undefined;
    let rowError:     string | undefined;

    if (storagePath) {
      const { error } = await supabase.storage
        .from('training-videos')
        .remove([storagePath]);
      if (error) storageError = error.message;
    }

    const { error } = await supabase
      .from('training_videos')
      .delete()
      .eq('id', videoId);
    if (error) rowError = error.message;

    return { ok: !storageError && !rowError, storageError, rowError };
  },
};
